import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STATE_META } from "@/lib/fleet";
import { btnGhost, btnPrimary, inputCls } from "@/components/TopBar";

const STATUSES = [
  { value: "on_trip", label: "ON TRIP" },
  { value: "parked", label: "PARKED" },
  { value: "idle", label: "IDLE" },
  { value: "maintenance", label: "MAINTENANCE" },
  { value: "inactive", label: "INACTIVE" },
];

export const FleetStatusDialog = ({ vehicles, onClose, onSaved }) => {
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState("parked");
  const [busy, setBusy] = useState(false);

  const allSelected = selected.length === vehicles.length && vehicles.length > 0;

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return toast.error("Select at least one truck");
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/bulk-status", { vehicle_ids: selected, status });
      toast.success(`${data.updated} truck(s) set to ${status.replace("_", " ")}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1550] flex items-start justify-center overflow-y-auto p-4 py-10" data-testid="fleet-status-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 rise">
        <button
          type="button"
          data-testid="close-status-dialog-btn"
          onClick={onClose}
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">fleet control</p>
        <h2 className="font-head text-2xl font-black tracking-tight">SET STATUS</h2>
        <p className="mt-1 text-[11px] text-[#A1A1AA]">
          Anything other than ON TRIP parks the truck and holds its speed at 0 km/h - clients see the
          same status.
        </p>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <label className="overline">trucks · {selected.length} selected</label>
            <button
              type="button"
              data-testid="status-select-all-btn"
              onClick={() => setSelected(allSelected ? [] : vehicles.map((v) => v.id))}
              className={btnGhost}
            >
              {allSelected ? "CLEAR ALL" : "SELECT ALL"}
            </button>
          </div>
          <div className="doot-scroll mt-2 max-h-48 overflow-y-auto border border-white/15 bg-[#121212]" data-testid="status-vehicle-list">
            {vehicles.map((v) => {
              const meta = STATE_META[v.status === "on_trip" ? "moving" : v.status] || STATE_META.idle;
              return (
                <label
                  key={v.id}
                  data-testid={`status-vehicle-option-${v.plate_number}`}
                  className="flex cursor-pointer items-center gap-2 border-b border-white/5 px-3 py-2 text-[11px] hover:bg-[#1A1A1A]"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(v.id)}
                    onChange={() => toggle(v.id)}
                    className="h-3 w-3 accent-[#00F0FF]"
                  />
                  <span className="font-head font-bold">{v.plate_number}</span>
                  <span className="ml-auto text-[9px] tracking-[0.12em]" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <label className="overline mb-2 block">new status</label>
          <select
            data-testid="bulk-status-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={inputCls}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" disabled={busy} data-testid="submit-bulk-status-btn" className={`${btnPrimary} mt-6 w-full py-3`}>
          {busy ? "applying" : `apply to ${selected.length || 0} truck(s)`}
        </button>
      </form>
    </div>
  );
};
